import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { Button, Field, Input } from '@/components/ui';
import { errorText } from '@/lib/errors';

export function SignIn() {
  const { session } = useAuth();
  const loc = useLocation();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [cooldown, setCooldown] = useState(0);
  if (session) return <Navigate to={(loc.state as any)?.from ?? '/'} replace />;

  const send = async (e?: FormEvent) => {
    e?.preventDefault(); setErr(''); setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({ email: email.trim().toLowerCase(), options: { shouldCreateUser: true } });
      if (error) throw error;
      setStep('code'); setCooldown(60);
      const t = setInterval(() => setCooldown((c) => { if (c <= 1) { clearInterval(t); return 0; } return c - 1; }), 1000);
    } catch (x) { setErr(errorText(x)); } finally { setBusy(false); }
  };
  const verify = async (e: FormEvent) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      const { error } = await supabase.auth.verifyOtp({ email: email.trim().toLowerCase(), token: code.trim(), type: 'email' });
      if (error) throw error;
    } catch (x) { setErr(errorText(x)); } finally { setBusy(false); }
  };
  return (
    <div className="signin">
      <div className="signin-card">
        <div className="brand"><span className="brand-mark">बही</span><span className="brand-name">Books</span></div>
        {step === 'email' ? (
          <form onSubmit={send}>
            <h1>Sign in</h1>
            <p className="sub">Enter your email. We will send a 6-digit code; there is no password.</p>
            <Field label="Email" required><Input type="email" autoComplete="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
            {err && <p className="hint error">{err}</p>}
            <Button variant="primary" type="submit" busy={busy}>Send code</Button>
          </form>
        ) : (
          <form onSubmit={verify}>
            <h1>Enter the code</h1>
            <p className="sub">Sent to <b>{email}</b>. It works for 15 minutes.</p>
            <Field label="6-digit code" required><Input inputMode="numeric" pattern="[0-9]{6}" maxLength={6} autoFocus required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} className="code" /></Field>
            {err && <p className="hint error">{err}</p>}
            <div className="row">
              <Button variant="primary" type="submit" busy={busy}>Sign in</Button>
              <Button variant="ghost" disabled={cooldown > 0} onClick={() => send()}>{cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}</Button>
              <Button variant="link" onClick={() => setStep('email')}>Change email</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
